import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useGetStudyQuery } from '@/state/slices/studies';
import {
  useGetReportQuery,
  useGetReportEventsQuery,
  useGetStudyReportsQuery,
  useGetTemplateQuery,
} from '@/state/slices/reports';
import { useTrackViewMutation } from '@/state/slices/views';
import { useReportTimeline } from '@/features/report-history';
import { SettingsContext } from '@/state/contexts';
import { usePageLoading } from '@/hooks/usePageLoading';
import { ageInYears } from '@/shared/utils/patientAge';
import { buildMeasurementGroups } from '../utils/buildMeasurementGroups';
import { buildFindingGroups } from '../utils/buildFindingGroups';
import { normalizeStudyAddendums } from '../utils/normalizeAddendums';
import type {
  MeasurementGroupDisplay,
  FindingGroupDisplay,
  NormalizedAddendum,
} from '../types/reportDetail';

/**
 * Main data orchestrator for the Report Detail page.
 * Composes RTK Query hooks, timeline state, and computed display data.
 */
export function useReportDetailData(studyUuid: string | undefined) {
  const { settings } = useContext(SettingsContext);
  const [trackView] = useTrackViewMutation();
  const flashTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { data: study, isLoading: isLoadingStudy } = useGetStudyQuery(
    { uuid: studyUuid || '' },
    { skip: !studyUuid }
  );

  const templateUuid = settings.defaultTemplate;
  const { data: template, isLoading: isLoadingTemplate } = useGetTemplateQuery(
    templateUuid || '',
    { skip: !templateUuid }
  );

  const { data: reportEventsPagination } = useGetReportEventsQuery(
    studyUuid || '',
    { skip: !studyUuid }
  );

  const { data: studyReportsPagination } = useGetStudyReportsQuery(
    studyUuid || '',
    { skip: !studyUuid }
  );

  // ── Timeline state ───────────────────────────
  const {
    entries: timelineEntries,
    events: timelineEvents,
    selectedEventId,
    selectEvent,
    selectedEvent,
    expandedGroupIds,
    toggleGroup,
  } = useReportTimeline(
    reportEventsPagination?.results ?? [],
    studyReportsPagination?.results ?? []
  );

  const activeReportUuid = selectedEvent?.reportUuid ?? study?.reportUuid;
  const { data: report, isLoading: isLoadingReport } = useGetReportQuery(
    activeReportUuid || '',
    { skip: !activeReportUuid }
  );

  usePageLoading(isLoadingStudy || isLoadingReport || isLoadingTemplate);

  useEffect(() => {
    if (report?.uuid) {
      trackView({ objectType: 'REPORT', objectUuid: report.uuid });
    }
  }, [report?.uuid, trackView]);

  useEffect(
    () => () => {
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    },
    []
  );

  const handleTimelineSelect = useCallback(
    (eventId: string) => {
      selectEvent(eventId);
      const event = timelineEvents.find((e) => e.id === eventId);
      if (!event) return;

      if (event.kind === 'ADDENDUM_ADDED') {
        const anchor = document.getElementById(
          `addendum-report-${event.reportUuid}`
        );
        if (anchor) {
          if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
          anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
          anchor.classList.add('highlightFlash');
          flashTimeoutRef.current = setTimeout(() => {
            anchor.classList.remove('highlightFlash');
            flashTimeoutRef.current = null;
          }, 2000);
        }
      }
    },
    [selectEvent, timelineEvents]
  );

  // AI values fill blank rows on every report, finalized or not, so a signed
  // report reads the same as the draft did rather than losing rows once it is
  // signed. AI-sourced rows stay marked, so what a reader adopted is still
  // distinguishable from what the scan produced.
  //
  // Note this intentionally departs from the exported PDF, which excludes AI
  // values on a FINAL report (`exports/mixins.py`) — a signed PDF is a fixed
  // document, this view is not.
  const measurementGroups: MeasurementGroupDisplay[] = useMemo(
    () =>
      buildMeasurementGroups(
        template,
        report?.measurements ?? [],
        study?.patientSex,
        report?.aiMeasurements ?? [],
        ageInYears(study?.patientDateOfBirth, study?.studyDate)
      ),
    [
      template,
      report?.measurements,
      report?.aiMeasurements,
      study?.patientSex,
      study?.patientDateOfBirth,
      study?.studyDate,
    ]
  );

  const findingGroups: FindingGroupDisplay[] = useMemo(
    () => buildFindingGroups(template, report?.findings ?? []),
    [template, report?.findings]
  );

  const addendums: NormalizedAddendum[] = useMemo(
    () =>
      normalizeStudyAddendums(studyReportsPagination?.results ?? [], report),
    [studyReportsPagination, report]
  );

  const isSigned = !!report?.isFinalized;

  return {
    study,
    report,
    template,
    isSigned,
    isLoading: isLoadingStudy || isLoadingReport || isLoadingTemplate,

    // Timeline
    timelineEntries,
    selectedEventId,
    selectedEvent,
    expandedGroupIds,
    handleTimelineSelect,
    toggleGroup,

    // Display data
    measurementGroups,
    findingGroups,
    addendums,
  };
}
